import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth_/callback")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Signing you in — PortDwellr" },
      { name: "description", content: "Completing sign-in to your PortDwellr yard workspace." },
      { property: "og:title", content: "Signing you in — PortDwellr" },
      { property: "og:description", content: "Completing sign-in to your PortDwellr yard workspace." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    // The confirmation link carries the session in the URL fragment; supabase-js
    // consumes it on load (detectSessionInUrl). Wait for a real session, then
    // hand off to "/" — which now resolves to the Gate Manifest.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active && session) navigate({ to: "/", replace: true });
    });

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) {
        navigate({ to: "/", replace: true });
      } else {
        // No fragment / already-consumed link: send them to sign in.
        window.setTimeout(() => {
          if (active) {
            void supabase.auth.getSession().then(({ data: retry }) => {
              if (!active) return;
              if (retry.session) navigate({ to: "/", replace: true });
              else {
                setError("This sign-in link is no longer valid. Sign in again to continue.");
                navigate({ to: "/auth", replace: true });
              }
            });
          }
        }, 1500);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-paper px-6">
      <Logo />
      <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-ink-soft">
        {error ?? "Confirming your account…"}
      </p>
    </div>
  );
}
